<script lang="ts">
  import { onDestroy } from "svelte";
  import type { VideoDetails } from "../helpers/mediaHelpers";
  import { isInRange, cutIntersects } from "../helpers/videoHelpers";
  import Timeline from "./Timeline.svelte";
  import Cropper from "./Cropper.svelte";
  import ExportForm from "./ExportForm.svelte";
  import Modal from "./Modal.svelte";

  export let videoUrl: string;
  export let videoDetails: VideoDetails;
  export let onDiscard: () => void;
  export let isPlaying = false;
  let isBlade = false;
  let cuts = [];

  let video: HTMLVideoElement;

  const toggleBlade = () => {
    isBlade = !isBlade;
  };

  const handleKeydown = (event) => {
    switch (event.key) {
      case " ":
        onPlause();
        break;
      case "b":
        toggleBlade();
        break;
      default:
        break;
    }
  };

  const onPlause = () => {
    if (isPlaying) {
      isPlaying = false;
      video.pause();
    } else {
      isPlaying = true;
      video.play();
    }
  };

  let trim = { start: 0, end: videoDetails.duration };
  const setTrim = ({ start, end }) => {
    const newTrim = {
      start: start * videoDetails.duration,
      end: end * videoDetails.duration,
    };
    if (video && newTrim.start !== trim.start) {
      video.currentTime = newTrim.start;
    } else if (video && newTrim.end !== trim.end) {
      video.currentTime = Math.max(newTrim.start, newTrim.end - 0.25);
    }
    trim = newTrim;
  };

  const setVideoTime = (time) => {
    video.currentTime = time * videoDetails.duration;
  };

  let crop = {
    left: 0,
    top: 0,
    width: videoDetails.width,
    height: videoDetails.height,
  };
  const setCrop = (newCrop) =>
    (crop = {
      left: newCrop.left * videoDetails.width,
      top: newCrop.top * videoDetails.height,
      width: newCrop.width * videoDetails.width,
      height: newCrop.height * videoDetails.height,
    });

  let currentTime = 0;
  const interval = setInterval(() => {
    currentTime = video.currentTime;
    let cutIdx = cutIntersects(cuts, currentTime / videoDetails.duration);
    if(!video.paused) {
      if (cuts.length && (typeof cutIdx == 'number')) {
        setVideoTime(cuts[cutIdx].end + 0.01)
      } else if (currentTime - trim.start < -0.001 || currentTime > trim.end) {
        video.currentTime = trim.start;
      }
      }

  }, 1000 / 60);
  onDestroy(() => clearInterval(interval));
</script>

<style>
  .container {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }

  header {
    background-color: var(--bg);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    height: 5rem;
  }

  .preview-area {
    display: flex;
    flex-shrink: 2;
    flex-grow: 2;
    text-align: center;
    overflow: hidden;
    justify-content: center;
    padding: 1rem;
  }

  video {
    height: 100%;
    width: auto;
  }
</style>

<svelte:window on:keydown={handleKeydown} />
<Modal>
  <div
    class="container"
    style="--current-time: {currentTime / videoDetails.duration};">
    <div class="preview-area">
      <Cropper onCropChange={setCrop}>
        <!-- svelte-ignore a11y-media-has-caption -->
        <video src={videoUrl} bind:this={video} playsinline loop />
      </Cropper>
    </div>

    <header>
      <button class="discard-button" on:click={onDiscard}>Discard Recording</button>
      {#if video}
        <button
          class="play-pause"
          on:click={onPlause}>{isPlaying ? 'Pause' : 'Play'}</button>
      {/if}
      <ExportForm srcVideoUrl={videoUrl} {trim} {cuts} {videoDetails} {crop} />
    </header>

    <Timeline
      onTrimChange={setTrim}
      {isPlaying}
      {isBlade}
      bind:cuts
      setTime={setVideoTime} />
  </div>
</Modal>
